import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Shell } from './layout/Shell'
import { LoginPage } from './features/login/LoginPage'
import { CriarContaPage } from './features/login/CriarContaPage'
import { EsqueciSenhaPage, RedefinirSenhaPage } from './features/login/RecuperarSenhaPage'
import { HomePage } from './features/institucional/HomePage'
import { DuvidasPage } from './features/institucional/DuvidasPage'
import { PluggyPage as PluggyInstitucionalPage } from './features/institucional/PluggyPage'
import { PrecosPage } from './features/institucional/PrecosPage'
import { CookiesPage } from './features/institucional/CookiesPage'
import { ContatoPage } from './features/institucional/ContatoPage'
import { BaixarAppPage } from './features/institucional/BaixarAppPage'
import { PlanoPage } from './features/plano/PlanoPage'
import { AdminPage } from './features/admin/AdminPage'
import { AdminConfigShell } from './layout/AdminConfigShell'
import { PlanosPage, MeiosDePagamentoPage, EmailPage, ContatosPage, FaqPage, GeralPage, ArmazenamentoPage } from './features/adminConfig/AdminConfigPage'
import { RecursosPlanoPage } from './features/adminConfig/RecursosPlanoPage'
import { MonitorFipePage } from './features/adminConfig/MonitorFipePage'
import { ImportacaoPdfConfigPage } from './features/adminConfig/ImportacaoPdfConfigPage'
import { NotificacoesConfigPage } from './features/adminConfig/NotificacoesConfigPage'
import { PluggyConfigPage } from './features/adminConfig/PluggyConfigPage'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { ContasPage } from './features/contas/ContasPage'
import { TransacoesPage } from './features/transacoes/TransacoesPage'
import { NotasFiscaisPage } from './features/notas/NotasFiscaisPage'
import { GruposItensPage } from './features/notas/GruposItensPage'
import { GrupoDetalhePage } from './features/notas/GrupoDetalhePage'
import { SemGrupoPage } from './features/notas/SemGrupoPage'
import { ListasPage } from './features/listas/ListasPage'
import { CategoriasPage } from './features/categorias/CategoriasPage'
import { CartaoPage } from './features/cartoes/CartaoPage'
import { OrcamentoPage } from './features/orcamento/OrcamentoPage'
import { ProximosMesesPage } from './features/orcamento/ProximosMesesPage'
import { RelatoriosPage } from './features/relatorios/RelatoriosPage'
import { InsightsPage } from './features/insights/InsightsPage'
import { RecorrenciasPage } from './features/recorrencias/RecorrenciasPage'
import { RecorrenciaDetalhePage } from './features/recorrencias/RecorrenciaDetalhePage'
import { FinanciamentosPage } from './features/financiamentos/FinanciamentosPage'
import { FinanciamentoDetalhePage } from './features/financiamentos/FinanciamentoDetalhePage'
import { VeiculosPage } from './features/veiculos/VeiculosPage'
import { VeiculoDetalhePage } from './features/veiculos/VeiculoDetalhePage'
import { AbastecimentosPage } from './features/veiculos/AbastecimentosPage'
import { DespesasPage } from './features/veiculos/DespesasPage'
import { ManutencoesPage } from './features/veiculos/ManutencoesPage'
import { InsightsVeiculoPage } from './features/veiculos/InsightsVeiculoPage'
import { MetasPage } from './features/metas/MetasPage'
import { PluggyContasPage } from './features/pluggy/PluggyContasPage'
import { CompartilhamentoPage } from './features/compartilhamento/CompartilhamentoPage'
import { ConfiguracoesPage } from './features/configuracoes/ConfiguracoesPage'
import { UsuariosPage } from './features/usuarios/UsuariosPage'
import { TutoriaisPage } from './features/tutorial/TutoriaisPage'
import { renovador } from './api/client'
import { useAuthStore } from './store/authStore'
import { ToastProvider } from './components/ui/Toast'
import type { ReactNode } from 'react'

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
})

/**
 * Sessão viva sem a pessoa perceber: ao abrir e ao voltar para a aba, renova o token de acesso se ele já venceu
 * ou está para vencer. Assim a primeira tela depois de um tempo parado não pede dados com token vencido.
 */
function useManterSessao(autenticado: boolean) {
  useEffect(() => {
    if (!autenticado) return
    void renovador.renovarSeVencendo()
    const aoVoltar = () => { if (document.visibilityState === 'visible') void renovador.renovarSeVencendo() }
    document.addEventListener('visibilitychange', aoVoltar)
    return () => document.removeEventListener('visibilitychange', aoVoltar)
  }, [autenticado])
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  useManterSessao(isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/precos" element={<PrecosPage />} />
          <Route path="/duvidas" element={<DuvidasPage />} />
          <Route path="/conectar-bancos" element={<PluggyInstitucionalPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/contato" element={<ContatoPage />} />
          <Route path="/baixar-app" element={<BaixarAppPage />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/criar-conta" element={<CriarContaPage />} />
          <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
          <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />

          <Route
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/contas" element={<ContasPage />} />
            <Route path="/transacoes" element={<TransacoesPage />} />
            <Route path="/notas-fiscais" element={<NotasFiscaisPage />} />
            <Route path="/grupos-itens" element={<GruposItensPage />} />
            <Route path="/grupos-itens/:id" element={<GrupoDetalhePage />} />
            <Route path="/itens-sem-grupo" element={<SemGrupoPage />} />
            <Route path="/listas" element={<ListasPage />} />
            <Route path="/listas/:id" element={<ListasPage />} />
            <Route path="/categorias" element={<CategoriasPage />} />
            <Route path="/cartoes" element={<CartaoPage />} />
            <Route path="/orcamento" element={<OrcamentoPage />} />
            <Route path="/orcamento/proximos-meses" element={<ProximosMesesPage />} />
            <Route path="/relatorios" element={<RelatoriosPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/recorrencias" element={<RecorrenciasPage />} />
            <Route path="/recorrencias/:id" element={<RecorrenciaDetalhePage />} />
            <Route path="/financiamentos" element={<FinanciamentosPage />} />
            <Route path="/financiamentos/:id" element={<FinanciamentoDetalhePage />} />
            <Route path="/veiculos" element={<VeiculosPage />} />
            <Route path="/veiculos/:id" element={<VeiculoDetalhePage />} />
            <Route path="/veiculos/:id/abastecimentos" element={<AbastecimentosPage aba="lista" />} />
            <Route path="/veiculos/:id/consumo" element={<AbastecimentosPage aba="consumo" />} />
            <Route path="/veiculos/:id/despesas" element={<DespesasPage />} />
            <Route path="/veiculos/:id/manutencoes" element={<ManutencoesPage />} />
            <Route path="/veiculos/:id/insights" element={<InsightsVeiculoPage />} />
            <Route path="/metas" element={<MetasPage />} />
            <Route path="/pluggy" element={<PluggyContasPage />} />
            <Route path="/plano" element={<PlanoPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/compartilhamento" element={<CompartilhamentoPage />} />
            <Route path="/configuracoes" element={<ConfiguracoesPage />} />
            <Route path="/usuarios" element={<UsuariosPage />} />
            <Route path="/tutoriais" element={<TutoriaisPage />} />
          </Route>

          <Route
            path="/admin/config"
            element={
              <ProtectedRoute>
                <AdminConfigShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="planos" replace />} />
            <Route path="planos" element={<PlanosPage />} />
            <Route path="recursos" element={<RecursosPlanoPage />} />
            <Route path="fipe" element={<MonitorFipePage />} />
            <Route path="pagamentos" element={<MeiosDePagamentoPage />} />
            <Route path="email" element={<EmailPage />} />
            <Route path="contatos" element={<ContatosPage />} />
            <Route path="faq" element={<FaqPage />} />
            <Route path="geral" element={<GeralPage />} />
            <Route path="armazenamento" element={<ArmazenamentoPage />} />
            <Route path="importacao-pdf" element={<ImportacaoPdfConfigPage />} />
            <Route path="notificacoes" element={<NotificacoesConfigPage />} />
            <Route path="pluggy" element={<PluggyConfigPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  )
}
